import { useCallback } from 'react';
import type { notification } from 'antd';
import { useLazyGetGuidelineUrlQuery } from '@/state/slices/reports';

type NotificationApi = ReturnType<typeof notification.useNotification>[0];

const FAILED_TITLE = 'Could not open the guideline';

/**
 * Open a guideline PDF from the worksheet's range popover, at the cited page.
 *
 * The bucket is private, so the backend signs a short-lived URL per click.
 * The tab is opened synchronously on the click (popup blockers allow that)
 * and pointed at the signed URL once it arrives; when the link cannot be
 * signed the tab closes again and the reader is told, and a blocked popup
 * is reported rather than silently dropped. `noopener` is applied by hand:
 * passing it as a window feature makes window.open return null, which would
 * leave the tab blank. The page goes in the URL fragment, which browsers'
 * PDF viewers honour and which is not part of the signed request.
 */
export function useOpenGuideline(
  notify: NotificationApi
): (file: string, page?: number | null) => void {
  const [getGuidelineUrl] = useLazyGetGuidelineUrlQuery();
  return useCallback(
    (file: string, page?: number | null) => {
      const tab = window.open('', '_blank');
      if (tab) tab.opener = null;
      getGuidelineUrl(file)
        .unwrap()
        .then(({ url }) => {
          const target = page ? `${url}#page=${page}` : url;
          if (tab) {
            tab.location.href = target;
            return;
          }
          // The synchronous open was blocked; retry now that we have the URL,
          // and say so if that is blocked too.
          if (!window.open(target, '_blank', 'noopener,noreferrer')) {
            notify.error({
              message: FAILED_TITLE,
              description:
                'Your browser blocked the new tab. Allow pop-ups for this site and try again.',
            });
          }
        })
        .catch(() => {
          tab?.close();
          notify.error({
            message: FAILED_TITLE,
            description:
              'The link could not be created right now. Try again in a moment.',
          });
        });
    },
    [getGuidelineUrl, notify]
  );
}
